import { GetServerSideProps } from 'next';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import Purchase from '@lib/Purchase';
import { NoodlePage } from '@typings/NextJS';
import { getProductBySlug } from '@tsClient';
import { Interval } from '@typings/graphql-models';
import handlePageRequest from '@helpers/handlePageRequest';
import InternalNotFoundError from '@helpers/InternalNotFoundError';
import { ErrorGateSSRProps } from '@context/ErrorContext';
import { DEFAULT_OG_IMAGE, getSeoImages, getSeoUrl, SeoSSRProps } from '@providers/SEO';
import logger from '@helpers/logger/server';
import { usePageReloader } from '@hooks';

type PropsFromSSR =
  {
    creatorSlug: string;
    purchase: Awaited<ReturnType<typeof getProductBySlug>> | null;
    interval: string | null;
    priceId: string | null;
  }
  & ErrorGateSSRProps;

type PropsGatedByErrorGate = {
  creatorSlug: string;
  priceId: string | null;
  interval: string | null;
  purchase: Exclude<PropsFromSSR['purchase'], null>;
  isFreeMembership: boolean;
};

type Params = {
  creatorSlug: string;
  productSlug: string;
};

const PurchasePage: NoodlePage<PropsGatedByErrorGate> = ({
  creatorSlug,
  priceId,
  purchase,
  interval,
  isFreeMembership=false,
}) => {
  usePageReloader({ // if there is no creatorSlug getServerSideProps failed, reload
    hasData: Boolean(creatorSlug),
    page: 'PurchasePage',
  });

  if (!creatorSlug) {
    return null;
  }

  return <Purchase priceId={priceId} interval={interval} data={purchase} isFreeMembership={isFreeMembership} />;
};

export const getServerSideProps: GetServerSideProps<PropsFromSSR, Params> = async (context) => {
  const creatorSlug = context?.params?.creatorSlug as string;
  const productSlug = context?.params?.productSlug as string;
  const interval = deserializeQueryStringItem(context?.query?.interval, {
    enum: Interval,
    type: 'enum',
  });
  const priceId = deserializeQueryStringItem(context?.query?.priceId);

  const productResponse = await handlePageRequest(() => getProductBySlug({ creatorSlug, productSlug }));

  let ssrError = productResponse.error;
  const product = productResponse.data;

  if (!ssrError && !product) {
    ssrError = InternalNotFoundError.createForSSR();
  }

  let seo: SeoSSRProps['seo'] = null;
  if (product) {
    seo = {
      description: product.shortDescription,
      images: getSeoImages(product?.image?.url || product?.creator?.ogImage?.url || DEFAULT_OG_IMAGE),
      title: product.title,
      url: getSeoUrl(context),
    };
  }

  logger.info(
    {
      extracted: { creatorSlug, productSlug },
      hasUndefined: !creatorSlug || !productSlug,
      page: 'PurchasePage',
      params: context?.params || 'none',
      url: context?.req?.url || 'unknown-url',
    },
    'getServerSideProps',
  );

  return {
    props: {
      creatorSlug,
      interval,
      isFreeMembership: context?.query?.isFreeMembership || false,
      priceId,
      purchase: product,
      seo,
      ssrError,
    },
  };
};

export default PurchasePage;
